import React, { useState, useEffect } from 'react';
import { X, Phone, MessageSquare, Send, AlertCircle, CheckCircle, ToggleLeft, ToggleRight } from 'lucide-react';
import { triggerInboundConversation } from '../services/api';
import { isValidPhoneNumber, PHONE_NUMBER_ERROR } from '../utils/phoneValidation';

const TriggerInboundModal = ({ isOpen, onClose, onConversationTriggered, selectedStore }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [sendRealMessage, setSendRealMessage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setPhoneNumber('');
      setMessage('');
      setSendRealMessage(false);
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    if (!isValidPhoneNumber(phoneNumber)) {
      setError(PHONE_NUMBER_ERROR);
      return;
    }

    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (!selectedStore) {
      setError('Please select a store from the store selection section first.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    setError('');
    try {
      const response = await triggerInboundConversation(
        phoneNumber.trim(),
        selectedStore,
        message.trim(),
        sendRealMessage
      );

      setSuccess(`Inbound conversation triggered for ${response.phoneNumber} (Lead #${response.leadId})`);
      if (onConversationTriggered) {
        onConversationTriggered(response);
      }

      // Close after a short delay so the user can see the success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error triggering inbound conversation:', err);
      setError(err.message || 'Failed to trigger inbound conversation');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <MessageSquare size={18} className="mr-2" />
            Trigger Inbound Conversation
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {success && (
            <div className="flex items-center p-3 bg-green-50 text-green-700 rounded-lg text-sm">
              <CheckCircle size={16} className="mr-2 flex-shrink-0" />
              {success}
            </div>
          )}

          {error && (
            <div className="flex items-center p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle size={16} className="mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="phoneNumber">
              Customer Phone Number
            </label>
            <div className="relative">
              <Phone size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+15551234567"
                aria-invalid={Boolean(phoneNumber.trim() && !isValidPhoneNumber(phoneNumber))}
                disabled={submitting}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="message">
              Inbound Message
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type the simulated customer message..."
              disabled={submitting}
              rows={4}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-700">Send real SMS reply</span>
              <p className="text-xs text-gray-500">
                {sendRealMessage
                  ? 'AI response will be sent to the customer via Bandwidth.'
                  : 'AI response will be simulated and not sent.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSendRealMessage(!sendRealMessage)}
              disabled={submitting}
              className={`flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${
                sendRealMessage
                  ? 'bg-green-100 text-green-700'
                  : 'bg-orange-100 text-orange-700'
              }`}
            >
              {sendRealMessage ? (
                <>
                  <ToggleRight size={16} className="mr-1" /> On
                </>
              ) : (
                <>
                  <ToggleLeft size={16} className="mr-1" /> Off
                </>
              )}
            </button>
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Triggering...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Trigger
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TriggerInboundModal;
